import { Input2 } from 'stories/components'
import { getPrice2decimal, removeComma, solveDecimalJavascriptSum } from 'utils'

/** Someone who funds a share of an advance. */
export interface DrawParticipant {
  /** `servicing_participation.id`. 0 for the lender's own unsold share. */
  participantId: number
  participantUserId: string
  participantName: string
  /** The platform's own figure: outstanding capital over the loan's current balance. */
  ownershipPct: number
}

/** One participant's share, as the draw stores it. */
export interface DrawAllocation {
  participantId: number
  participantUserId?: string
  participantName: string
  amount: number
}

/** Half a cent. A split that rounds still balances; the server applies the same tolerance. */
export const ALLOCATION_TOLERANCE = 0.005

/**
 * The fields of a draw that decide how much principal it advances, as the form holds them: a
 * thousand-separated string while being typed, a number once converted, and absent on a bucket
 * whose grid has no fee columns.
 */
export type DrawAdvanceField = string | number | undefined

export interface DrawAdvanceFields {
  payment?: DrawAdvanceField
  deposit?: DrawAdvanceField
  inspectionFee?: DrawAdvanceField
  wireFee?: DrawAdvanceField
  titleUpdateFee?: DrawAdvanceField
  additionHoldback?: DrawAdvanceField
}

/**
 * What a draw actually advances: the money paid out PLUS the fees and holdback funded with it,
 * less any deposit handed back against it.
 *
 * This, not the payment on its own, is what the investors fund. A $100,000 draw charging a $100
 * inspection fee writes `payment` 99,900 and `inspectionFee` 100 — the contractor is handed 99,900
 * and the inspector is paid the 100 — but the borrower owes $100,000 and the loan's balance grows
 * by $100,000. Splitting the payment alone credited the investors with 99,900 and quietly dropped
 * the fee onto the lender's unsold share, on every draw that charges one.
 *
 * Mirrors the backend's `principalAdvanceAmount` (`utils/principal-advance.ts`) and the overdraw
 * check's own "fees leave the bucket with the draw", so the screen, the server and the balance all
 * state the same advance.
 */
export const drawAdvanceAmount = (fields: DrawAdvanceFields): number => {
  const num = (v: DrawAdvanceField) => (v === undefined || v === null || v === '' ? 0 : removeComma(v))
  return solveDecimalJavascriptSum([
    num(fields?.payment),
    num(fields?.inspectionFee),
    num(fields?.wireFee),
    num(fields?.titleUpdateFee),
    num(fields?.additionHoldback),
    -1 * num(fields?.deposit),
  ])
}

/** The lender's unsold share has no id, so its name stands in as the key. */
export const participantKey = (p: DrawParticipant) =>
  `${p.participantId || `lender:${p.participantName}`}`

/**
 * Split an amount across participants at each one's actual percentage of the loan.
 *
 * Deliberately NOT normalised across the listed participants. The server adds whatever has not
 * been sold down as its own entry precisely so these percentages total 100, and normalising
 * would paper over the case where they do not: a loan whose one listed participant holds 46%
 * would have them defaulted to funding the whole advance, and it would look balanced.
 *
 * The last row takes the remainder instead of its own rounded share, but only when the
 * percentages already total 100, so a split lands exactly on the draw. When they total less, the
 * shortfall stays visible as Undistributed, which is the honest answer.
 */
export const splitByOwnership = (
  participants: DrawParticipant[],
  total: number,
): Record<string, string> => {
  const next: Record<string, string> = {}
  if (!participants.length || !(total > 0)) return next
  const totalPct = participants.reduce((sum, p) => sum + (Number(p.ownershipPct) || 0), 0)
  if (!(totalPct > 0)) return next
  const coversWholeLoan = Math.abs(totalPct - 100) < 0.01

  let running = 0
  participants.forEach((p, index) => {
    const key = participantKey(p)
    if (coversWholeLoan && index === participants.length - 1) {
      next[key] = (Math.round((total - running) * 100) / 100).toFixed(2)
      return
    }
    const share = Math.round(((Number(p.ownershipPct) || 0) / 100) * total * 100) / 100
    running = Math.round((running + share) * 100) / 100
    next[key] = share.toFixed(2)
  })
  return next
}

/** What has been allocated so far. */
export const allocatedTotal = (allocation: Record<string, string>): number =>
  solveDecimalJavascriptSum(Object.values(allocation).map((v) => removeComma(v)))

/** The allocation as the draw stores it: named shares, zero-value rows dropped. */
export const toAllocations = (
  participants: DrawParticipant[],
  allocation: Record<string, string>,
): DrawAllocation[] =>
  participants
    .map((p) => ({
      participantId: p.participantId,
      participantUserId: p.participantUserId,
      participantName: p.participantName,
      amount: removeComma(allocation[participantKey(p)] || ''),
    }))
    .filter((row) => row.amount !== 0)

/**
 * Who funded an advance and for how much.
 *
 * Shared by the pipeline's Draw modal and the loan's own Construction Reserve form so a draw
 * records its funding the same way wherever it is entered. The split is defaulted by ownership
 * and freely editable; the running total is shown because the server refuses a split that does
 * not add up to the draw, and discovering that on submit would be worse than seeing it here.
 */
export const DrawFundedBy = ({
  participants,
  allocation,
  drawAmount,
  touched = false,
  onChange,
  onResetToOwnership,
}: {
  participants: DrawParticipant[]
  allocation: Record<string, string>
  /** The draw's full advance (`drawAdvanceAmount`), not the payment: the fees are funded too. */
  drawAmount: number
  /** True once a share has been typed over, so the caller has stopped re-splitting. */
  touched?: boolean
  onChange: (key: string, value: string) => void
  onResetToOwnership: () => void
}) => {
  const allocated = allocatedTotal(allocation)
  const undistributed = Math.round((drawAmount - allocated) * 100) / 100
  const balances = Math.abs(undistributed) <= ALLOCATION_TOLERANCE
  const money = (v: number) => getPrice2decimal(v, false, true)

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <span className="font-bold">Funded by</span>
        {participants.length > 0 && (
          <button type="button" className="text-sm text-shade-blue hover:underline" onClick={onResetToOwnership}>
            Distribute by ownership
          </button>
        )}
      </div>

      {participants.length === 0 ? (
        <p className="text-sm text-gray-500">
          This loan has no participations on file, so the whole advance is the lender&apos;s.
        </p>
      ) : (
        <>
          <table className="text-sm w-full max-w-2xl">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left font-medium px-2 py-1">Participant</th>
                <th className="text-right font-medium px-2 py-1">Ownership</th>
                <th className="text-right font-medium px-2 py-1 w-40">Amount</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((p) => {
                const key = participantKey(p)
                return (
                  <tr key={key} className="border-b border-gray-100">
                    <td className="px-2 py-1">{p.participantName}</td>
                    <td className="px-2 py-1 text-right text-gray-500">
                      {(Number(p.ownershipPct) || 0).toFixed(2)}%
                    </td>
                    <td className="px-2 py-1">
                      <Input2
                        title=""
                        type="thousandSep"
                        prefix="$"
                        value={allocation[key] || ''}
                        onChange={(value: string) => onChange(key, value)}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className={`mt-2 text-sm ${balances ? 'text-gray-500' : 'text-red-600'}`}>
            Allocated {money(allocated)} of {money(drawAmount)} · Undistributed{' '}
            <span className="font-bold">{money(undistributed)}</span>
            {touched && <span className="text-gray-400"> · edited by hand, not following the draw amount</span>}
          </div>
        </>
      )}
    </div>
  )
}
